import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Percent, FileText, Download, Loader2, CheckCircle2, ExternalLink, Search } from 'lucide-react';
import { descargarFichero, obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL || '';
const token = () => obtenerToken();
const fmt = (x, d = 2) => (x == null || isNaN(x)) ? '-' : Number(x).toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d });

const CalculadoraTAE = () => {
  const [form, setForm] = useState({ capitalFinanciado: '', gastos: '', tinPct: '', plazoMeses: '' });
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const [calculando, setCalculando] = useState(false);
  const [formatos, setFormatos] = useState({ docx: true, pdf: false, md: false });
  const [expQuery, setExpQuery] = useState('');
  const [expOpciones, setExpOpciones] = useState([]);
  const [expSel, setExpSel] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [confirmacion, setConfirmacion] = useState(null);
  const debounceRef = useRef(null);

  const calcular = async () => {
    setError(''); setConfirmacion(null);
    const payload = {
      capitalFinanciado: parseFloat(form.capitalFinanciado),
      gastos: parseFloat(form.gastos || 0),
      tinPct: parseFloat(form.tinPct),
      plazoMeses: parseInt(form.plazoMeses, 10),
    };
    if (!payload.capitalFinanciado || !payload.tinPct || !payload.plazoMeses) {
      setResultado(null); return;
    }
    setCalculando(true);
    try {
      const res = await fetch(`${API}/api/tae/calcular`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Error al calcular'); setResultado(null); }
      else setResultado(data.resultado);
    } catch (e) { setError('Error de conexión'); }
    setCalculando(false);
  };

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(calcular, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [form]);

  const buscarExp = async (q) => {
    setExpQuery(q); setExpSel(null);
    if (q.length < 2) { setExpOpciones([]); return; }
    try {
      const res = await fetch(`${API}/api/expedientes?search=${encodeURIComponent(q)}&limit=8`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      setExpOpciones(data.data || data || []);
    } catch (e) { setExpOpciones([]); }
  };

  const generarInforme = async () => {
    if (!resultado) return;
    const fmts = Object.keys(formatos).filter((k) => formatos[k]).map((k) => (k === 'md' ? 'markdown' : k));
    if (!fmts.length) { setError('Selecciona al menos un formato'); return; }
    setGenerando(true); setError(''); setConfirmacion(null);
    try {
      const res = await fetch(`${API}/api/tae/informe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ resultado, formatos: fmts, expedienteId: expSel ? expSel.id : null }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.detail || 'Error al generar el informe');
      else setConfirmacion(data);
    } catch (e) { setError('Error de conexión'); }
    setGenerando(false);
  };

  const colorTae = resultado ? (resultado.taePct < 20 ? 'text-green-600' : resultado.taePct <= 25 ? 'text-orange-500' : 'text-red-600') : '';

  return (
    <div className="space-y-6" data-testid="calculadora-tae">
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-[#1F3864]">
            <Percent className="w-5 h-5" /> Calculadora TAE — Crédito al consumo (Ley 16/2011)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Capital financiado (€)</Label>
              <Input type="number" step="0.01" placeholder="Ej: 12148.06" data-testid="tae-capital"
                value={form.capitalFinanciado} onChange={(e) => setForm({ ...form, capitalFinanciado: e.target.value })} />
            </div>
            <div>
              <Label>Gastos incluidos (€)</Label>
              <Input type="number" step="0.01" placeholder="Ej: 1126.65" data-testid="tae-gastos"
                value={form.gastos} onChange={(e) => setForm({ ...form, gastos: e.target.value })} />
            </div>
            <div>
              <Label>TIN anual (%)</Label>
              <Input type="number" step="0.01" placeholder="Ej: 9.00" data-testid="tae-tin"
                value={form.tinPct} onChange={(e) => setForm({ ...form, tinPct: e.target.value })} />
            </div>
            <div>
              <Label>Plazo (meses)</Label>
              <Input type="number" placeholder="Ej: 48" data-testid="tae-plazo"
                value={form.plazoMeses} onChange={(e) => setForm({ ...form, plazoMeses: e.target.value })} />
              <div className="flex flex-wrap gap-1 mt-1">
                {[12, 24, 36, 48, 60, 72, 84, 96].map((m) => (
                  <button key={m} type="button" className="text-xs px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => setForm({ ...form, plazoMeses: String(m) })}>{m}</button>
                ))}
              </div>
            </div>
          </div>
          {error && <p className="text-red-600 text-sm mt-3" data-testid="tae-error">{error}</p>}
        </CardContent>
      </Card>

      {resultado && (
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col items-center mb-4">
              <span className="text-sm text-gray-500 uppercase tracking-wide">TAE resultante</span>
              <span className={`font-bold ${colorTae}`} style={{ fontSize: '48px', lineHeight: 1 }} data-testid="tae-resultado">
                {fmt(resultado.taePct, 3)}%
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[['TIN', `${fmt(resultado.tinPct, 2)}%`], ['Diferencial', `${fmt(resultado.diferencialPuntos, 3)} pp`],
                ['Cuota mensual', `${fmt(resultado.cuotaMensual)} €`], ['Capital neto', `${fmt(resultado.capitalNeto)} €`],
                ['Total pagado', `${fmt(resultado.totalPagado)} €`], ['Coste total', `${fmt(resultado.costeTotalCredito)} €`]].map(([k, v]) => (
                <div key={k} className="bg-gray-50 rounded p-3">
                  <div className="text-gray-500 text-xs">{k}</div>
                  <div className="font-semibold text-gray-900">{v}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 border-t pt-4">
              <Label>Expediente (opcional — para crear actuación automática)</Label>
              <div className="relative">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input placeholder="Buscar expediente por número o cliente…" value={expSel ? `${expSel.numero_expediente} — ${expSel.cliente_nombre || ''}` : expQuery}
                    onChange={(e) => buscarExp(e.target.value)} data-testid="tae-exp-search" />
                </div>
                {expOpciones.length > 0 && !expSel && (
                  <div className="absolute z-10 bg-white border rounded shadow w-full mt-1 max-h-52 overflow-auto">
                    {expOpciones.map((ex) => (
                      <button key={ex.id} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                        data-testid={`tae-exp-opt-${ex.id}`}
                        onClick={() => { setExpSel(ex); setExpOpciones([]); }}>
                        <b>{ex.numero_expediente}</b> — {ex.cliente_nombre || 'Sin cliente'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {expSel && <p className="text-xs text-green-700 mt-1">Se creará una actuación en {expSel.numero_expediente} ✓ <button className="underline ml-2" onClick={() => { setExpSel(null); setExpQuery(''); }}>quitar</button></p>}

              <div className="flex flex-wrap items-center gap-4 mt-4">
                <span className="text-sm font-medium">Formato:</span>
                {[['docx', 'Word (.docx)'], ['pdf', 'PDF'], ['md', 'Markdown']].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-1 text-sm cursor-pointer">
                    <input type="checkbox" checked={formatos[k]} data-testid={`tae-fmt-${k}`}
                      onChange={(e) => setFormatos({ ...formatos, [k]: e.target.checked })} /> {lbl}
                  </label>
                ))}
                <Button onClick={generarInforme} disabled={generando} data-testid="tae-generar-btn"
                  className="bg-[#1F3864] hover:bg-[#162a4d] text-white">
                  {generando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Generar Informe
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {confirmacion && (
        <Card>
          <CardContent className="p-6" data-testid="tae-confirmacion">
            <p className="text-green-700 font-medium flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5" /> Informe generado correctamente
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {(confirmacion.documentos || []).map((d) => (
                <button key={d.url || d.formato} type="button"
                  onClick={() => descargarFichero(`${API}${d.url}`, `informe-tae.${d.formato}`)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                  data-testid={`tae-descargar-${d.formato}`}>
                  <Download className="w-4 h-4" /> Descargar {d.label || d.formato.toUpperCase()}
                </button>
              ))}
            </div>
            {confirmacion.actuacion && (
              <a href={`/expedientes/${confirmacion.actuacion.expedienteId}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="tae-ver-expediente">
                <ExternalLink className="w-4 h-4" /> Actuación creada en el expediente ({confirmacion.actuacion.documentosAdjuntos} adjunto/s)
              </a>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CalculadoraTAE;
